import type { InjectionKey } from 'vue';
import { useTour } from '@dev_mat/floating-ui-tour';
import { useMutation } from '@vue/apollo-composable';
import { DISMISS_TOUR_INTRO } from '@/graphql';
import { useUserStore } from '@/store/userStore';
import { getTourPage } from './tourRegistry';
import {
  hasSeenPage,
  markPageSeen,
  hasSeenTriggerIntro,
  markTriggerIntroSeen,
  hasDismissedTriggerIntro,
  markTriggerIntroDismissed,
} from './tourStorage';
import { forceCleanupTourDom } from './tourCleanup';
import { isElementVisible } from './stepHelpers';
import './tourEntry';

// Polling tunables for the trigger-button intro popover. We wait for the
// trigger to actually mount (the shell loads user/school data first) before
// showing a one-time tooltip pointing at it.
const TRIGGER_INTRO_INITIAL_DELAY_MS = 400;
const TRIGGER_INTRO_POLL_INTERVAL_MS = 150;
const TRIGGER_INTRO_POLL_TIMEOUT_MS = 8000;
const TRIGGER_INTRO_TARGET = '#app-tour-trigger';

const APP_TOUR_KEY = Symbol('app-tour') as InjectionKey<AppTourApi>;

export interface AppTourApi {
  state: ReturnType<typeof useTour>['state'];
  hasTourForCurrent: ComputedRef<boolean>;
  showHint: Ref<boolean>;
  start: () => void;
  end: () => void;
  next: () => void;
  prev: () => void;
}

export { forceCleanupTourDom };

const noopApi: AppTourApi = {
  state: { isActive: false, currentStepIndex: -1, currentStep: null, totalSteps: 0 } as AppTourApi['state'],
  hasTourForCurrent: computed(() => false),
  showHint: ref(false),
  start: () => {},
  end: () => {},
  next: () => {},
  prev: () => {},
};

/**
 * Shell-level setup. Call once inside each dashboard shell
 * (`AdmiDashboardView`, `StudentDashboardView`) to wire route-driven step
 * swapping and lifecycle cleanup.
 */
export function provideAppTour(): AppTourApi {
  const route = useRoute();
  const tour = useTour([], {
    highlightTarget: true,
    defaultOffset: 12,
    overlayColor: 'rgba(0, 0, 0, 0.45)',
  });

  const currentRouteName = computed(() => (route.name as string | null) ?? null);
  const currentPage = computed(() => getTourPage(currentRouteName.value));
  const hasTourForCurrent = computed(() => !!currentPage.value);
  const showHint = ref(false);

  // "Don't show again" (#571) lives on the account, not the device. The intro
  // waits for the current user to load so it can never flash for someone who
  // opted out; a per-user cache covers a slow or failed load.
  const userStore = useUserStore();
  const currentUserId = computed(() => (userStore.currentUser?.id ? String(userStore.currentUser.id) : null));
  const preferenceLoaded = computed(() => userStore.currentUser !== null);
  // UserType is a union; only staff users and students carry the flag.
  const backendOptOut = computed(() => {
    const user = userStore.currentUser;
    return !!(user && 'tour_intro_dismissed_at' in user && user.tour_intro_dismissed_at);
  });
  const introOptedOut = computed(() => backendOptOut.value || hasDismissedTriggerIntro(currentUserId.value));
  const { mutate: dismissIntroMutate } = useMutation(DISMISS_TOUR_INTRO);

  let pollTimer: ReturnType<typeof setTimeout> | null = null;
  let pollCancelled = false;
  let triggerIntroShownThisSession = false;

  function refreshHint() {
    const page = currentPage.value;
    showHint.value = !!page && !hasSeenPage(page.key);
  }

  function clearPollTimer() {
    pollCancelled = true;
    if (pollTimer) {
      clearTimeout(pollTimer);
      pollTimer = null;
    }
  }

  function pollForElement(
    selector: string,
    expectedRoute: string | null,
    onReady: () => void,
  ) {
    pollCancelled = false;
    const startedAt = Date.now();

    const tick = () => {
      pollTimer = null;
      if (pollCancelled) return;
      if (currentRouteName.value !== expectedRoute) return;

      if (isElementVisible(selector)) {
        onReady();
        return;
      }
      if (Date.now() - startedAt >= TRIGGER_INTRO_POLL_TIMEOUT_MS) return;
      pollTimer = setTimeout(tick, TRIGGER_INTRO_POLL_INTERVAL_MS);
    };

    pollTimer = setTimeout(tick, TRIGGER_INTRO_INITIAL_DELAY_MS);
  }

  /**
   * One-shot popover that points at the "?" trigger and explains it. Runs
   * the first time the user lands on any page that has a tour, then never
   * again — they decide when (or whether) to start each page's actual tour.
   */
  /**
   * The user never wants the intro again: close it now, remember it for this
   * browser, and tell the backend. A failed save must not block anything —
   * the popover is already gone and the cache keeps it gone here.
   */
  function dismissTriggerIntroForGood() {
    const userId = currentUserId.value;
    // The request goes out first: a navigation right after the click would
    // abort an in-flight request, and nothing below can throw it away.
    dismissIntroMutate().catch(() => {
      // Cached locally below; the next login on another device shows the
      // intro once more, which is the documented fallback.
    });
    markTriggerIntroSeen(userId);
    markTriggerIntroDismissed(userId);
    const user = userStore.currentUser;
    if (user && 'tour_intro_dismissed_at' in user) {
      // Apollo freezes the query result; replace rather than mutate.
      userStore.currentUser = { ...user, tour_intro_dismissed_at: new Date().toISOString() };
    }
    clearPollTimer();
    if (tour.state.isActive) tour.end();
    forceCleanupTourDom();
  }

  /** The intro's body: the explanation plus the "Don't show again" control. */
  function buildTriggerIntroContent(): HTMLElement {
    const root = document.createElement('div');
    const text = document.createElement('p');
    text.textContent = 'Whenever a page has a guided walkthrough, this icon will pulse. Click it any time you want to learn what the current page can do — at your own pace.';
    root.appendChild(text);
    const dismiss = document.createElement('button');
    dismiss.type = 'button';
    dismiss.className = 'v-tour__button v-tour__button-dismiss';
    dismiss.setAttribute('data-testid', 'tour-intro-dismiss');
    dismiss.textContent = 'Don\'t show again';
    dismiss.addEventListener('click', dismissTriggerIntroForGood);
    root.appendChild(dismiss);
    return root;
  }

  function maybeShowTriggerIntro(expectedRoute: string | null) {
    if (triggerIntroShownThisSession) return;
    // Not decided yet: the user watcher below retries once the account loads.
    if (!preferenceLoaded.value) return;
    if (introOptedOut.value) return;
    if (hasSeenTriggerIntro(currentUserId.value)) return;

    triggerIntroShownThisSession = true;
    pollForElement(TRIGGER_INTRO_TARGET, expectedRoute, () => {
      if (currentRouteName.value !== expectedRoute) return;
      if (introOptedOut.value || hasSeenTriggerIntro(currentUserId.value)) return;
      const page = currentPage.value;
      if (!page) return;

      tour.defineSteps([
        {
          target: TRIGGER_INTRO_TARGET,
          title: 'Tours live here',
          content: () => buildTriggerIntroContent(),
          placement: 'bottom',
        },
      ]);
      tour.start();
      markTriggerIntroSeen(currentUserId.value);
    });
  }

  // The current user usually resolves after the first routed page: once it
  // does, give the intro its one chance on the page the user is looking at.
  watch(preferenceLoaded, (loaded) => {
    if (loaded && currentPage.value) {
      maybeShowTriggerIntro(currentRouteName.value);
    }
  });

  watch(
    currentPage,
    (page) => {
      clearPollTimer();
      if (tour.state.isActive) tour.end();
      forceCleanupTourDom();

      if (!page) {
        showHint.value = false;
        return;
      }

      const expectedRoute = currentRouteName.value;
      nextTick(() => {
        if (currentRouteName.value !== expectedRoute) return;
        tour.defineSteps(page.getSteps());
        refreshHint();
        maybeShowTriggerIntro(expectedRoute);
      });
    },
    { immediate: true },
  );

  function start() {
    const page = currentPage.value;
    if (!page) return;
    clearPollTimer();
    tour.defineSteps(page.getSteps());
    tour.start();
    markPageSeen(page.key);
    showHint.value = false;
  }

  function end() {
    clearPollTimer();
    tour.end();
    forceCleanupTourDom();
  }

  onBeforeUnmount(() => {
    clearPollTimer();
    if (tour.state.isActive) tour.end();
    forceCleanupTourDom();
  });

  const api: AppTourApi = {
    state: tour.state,
    hasTourForCurrent,
    showHint,
    start,
    end,
    next: tour.next,
    prev: tour.prev,
  };

  provide(APP_TOUR_KEY, api);
  return api;
}

/**
 * Consume the tour API provided by the nearest shell. Use this in the trigger
 * button or any UI inside the dashboard tree.
 */
export function useAppTour(): AppTourApi {
  return inject(APP_TOUR_KEY, noopApi);
}
